/** PROD20250602003-34: keep Theme Route sea-route preview from flooding the API. */

export type SeaRouteStop = { lng: number; lat: number };

export type SeaRouteLegResult = {
  route: { geometry: { coordinates: [number, number][] } };
  distance: number;
};

export type SeaRouteLeg = {
  origin: [number, number];
  destination: [number, number];
};

export const SEA_ROUTE_LEG_CACHE_PRECISION = 5;
/** Minimum gap between outbound leg requests (CrowdSec / WAF friendly). */
export const SEA_ROUTE_MIN_LEG_INTERVAL_MS = 500;
/** Debounce after stop edits before starting a recalc (when live calc is on). */
export const SEA_ROUTE_CALC_DEBOUNCE_MS = 1500;

export const fingerprintStops = (stops: SeaRouteStop[]): string =>
  stops
    .map(
      (stop) =>
        `${stop.lng.toFixed(SEA_ROUTE_LEG_CACHE_PRECISION)},${stop.lat.toFixed(SEA_ROUTE_LEG_CACHE_PRECISION)}`
    )
    .join('|');

export const buildLegCacheKey = (
  origin: [number, number],
  destination: [number, number]
): string =>
  `${origin[0].toFixed(SEA_ROUTE_LEG_CACHE_PRECISION)},${origin[1].toFixed(SEA_ROUTE_LEG_CACHE_PRECISION)}->${destination[0].toFixed(SEA_ROUTE_LEG_CACHE_PRECISION)},${destination[1].toFixed(SEA_ROUTE_LEG_CACHE_PRECISION)}`;

export const buildLegsFromStops = (stops: SeaRouteStop[]): SeaRouteLeg[] => {
  const legs: SeaRouteLeg[] = [];
  for (let i = 0; i < stops.length - 1; i += 1) {
    legs.push({
      origin: [stops[i].lng, stops[i].lat],
      destination: [stops[i + 1].lng, stops[i + 1].lat],
    });
  }
  return legs;
};

/**
 * Legs that still need a network fetch. Unchanged segments stay in the
 * session cache so edits only recompute dirty legs (Amisha / PROD20250602003-34).
 */
export const getLegsNeedingFetch = (
  legs: SeaRouteLeg[],
  cache: Map<string, SeaRouteLegResult>
): number[] => {
  const indexes: number[] = [];
  legs.forEach((leg, index) => {
    const key = buildLegCacheKey(leg.origin, leg.destination);
    if (!cache.has(key)) {
      indexes.push(index);
    }
  });
  return indexes;
};

export const resolveLegsFromCache = (
  legs: SeaRouteLeg[],
  cache: Map<string, SeaRouteLegResult>
): Array<SeaRouteLegResult | null> =>
  legs.map((leg) => {
    const key = buildLegCacheKey(leg.origin, leg.destination);
    return cache.get(key) ?? null;
  });

export const combineLegResults = (
  results: SeaRouteLegResult[]
): { coords: [number, number][]; distanceNm: number } => {
  const coords: [number, number][] = [];
  results.forEach((result, index) => {
    const segment = result.route.geometry.coordinates || [];
    if (!segment.length) return;
    if (index === 0) {
      coords.push(...segment);
    } else {
      coords.push(...segment.slice(1));
    }
  });
  const distanceNm = results.reduce(
    (sum, result) => sum + (result.distance || 0),
    0
  );
  return { coords, distanceNm };
};

export const straightLinePreview = (
  stops: SeaRouteStop[]
): { coords: [number, number][]; distanceNm: number } => {
  const coords = stops.map(
    (stop) => [stop.lng, stop.lat] as [number, number]
  );

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const nmPerKm = 0.5399568;
  const earthRadiusKm = 6371;
  const haversineNm = (a: SeaRouteStop, b: SeaRouteStop) => {
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return earthRadiusKm * c * nmPerKm;
  };

  const distanceNm = stops.reduce((sum, stop, idx) => {
    if (idx === 0) return 0;
    return sum + haversineNm(stops[idx - 1], stop);
  }, 0);

  return { coords, distanceNm };
};

export const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
